const EMPTY_CERTIFICATE = { available: false, cmid: null, url: null, label: "-" };

export const normalizeProgressValue = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const parsed = typeof value === "number"
    ? value
    : Number(String(value).replace(/,/g, ".").match(/-?\d+(?:\.\d+)?/)?.[0]);

  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.min(100, parsed));
};

const courseIdFor = (row) => Number(row?.courseid ?? row?.course_id ?? row?.id);

const normalizeCertificateCmid = (cmid) => {
  if (cmid === null || cmid === undefined || cmid === "") return null;

  if (typeof cmid === "number") return Number.isFinite(cmid) && cmid > 0 ? cmid : null;

  const trimmedCmid = String(cmid).trim();
  if (!trimmedCmid) return null;

  const numericCmid = Number(trimmedCmid);
  if (Number.isFinite(numericCmid)) return numericCmid > 0 ? numericCmid : null;

  return trimmedCmid;
};

const normalizeCertificate = (certificate, certificateCmid = null) => {
  const normalizedCertificateCmid = normalizeCertificateCmid(certificateCmid);

  if (!certificate) {
    return normalizedCertificateCmid
      ? { ...EMPTY_CERTIFICATE, available: true, cmid: normalizedCertificateCmid }
      : EMPTY_CERTIFICATE;
  }

  return {
    available: Boolean(certificate.available || certificate.cmid || certificate.id || certificate.url || normalizedCertificateCmid),
    cmid: certificate.cmid || certificate.id || normalizedCertificateCmid || null,
    url: certificate.url || null,
    label: certificate.label || certificate.name || "-",
  };
};

const normalizePluginRow = (row) => {
  const certificate = normalizeCertificate(row?.certificate || row?.certificado, row?.certificate_cmid);
  const progressPercent = normalizeProgressValue(row?.progressPercent ?? row?.progress ?? row?.completionpercent);

  return {
    finalGrade: row?.finalGrade ?? row?.finalgrade ?? row?.final_grade ?? row?.grade ?? "-",
    progressPercent,
    certificate,
    pluginRow: row || null,
  };
};

export const buildEmptyProgressRow = (course) => ({
  course,
  courseId: course?.id,
  courseName: course?.displayname || course?.fullname || "-",
  startdate: course?.startdate || 0,
  enddate: course?.enddate || 0,
  finalGrade: "-",
  progressPercent: null,
  certificate: EMPTY_CERTIFICATE,
  source: "fallback-pending",
});

export const buildFallbackProgressRow = (course, grade = {}, certificate = EMPTY_CERTIFICATE) => {
  const normalizedCertificate = normalizeCertificate(certificate);
  const progressPercent = normalizeProgressValue(grade.progressPercent);

  return {
    ...buildEmptyProgressRow(course),
    finalGrade: grade.finalGrade || "-",
    progressPercent: progressPercent ?? (normalizedCertificate.available ? 100 : null),
    certificate: normalizedCertificate,
    source: "fallback",
  };
};

export const mergeCoursesWithProgressSummary = (courses, summaryRows = []) => {
  const summaryByCourseId = new Map((Array.isArray(summaryRows) ? summaryRows : [])
    .map((row) => [courseIdFor(row), normalizePluginRow(row)]));

  return (Array.isArray(courses) ? courses : []).map((course) => {
    const pluginRow = summaryByCourseId.get(Number(course?.id));
    if (!pluginRow) return buildEmptyProgressRow(course);

    return {
      ...buildEmptyProgressRow(course),
      ...pluginRow,
      source: "plugin",
    };
  });
};

export const findRowsNeedingFallback = (rows) => (Array.isArray(rows) ? rows : []).filter((row) => (
  row.source === "fallback-pending" ||
  row.progressPercent === null
));

export const deriveProgressKpis = (rows) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  return safeRows.reduce((totals, row) => ({
    totalCourses: totals.totalCourses + 1,
    completedCourses: totals.completedCourses + (row.progressPercent >= 100 || row.certificate?.available ? 1 : 0),
    inProgressCourses: totals.inProgressCourses + (row.progressPercent > 0 && row.progressPercent < 100 ? 1 : 0),
  }), { totalCourses: 0, completedCourses: 0, inProgressCourses: 0 });
};

const valueForSort = (row, key) => {
  switch (key) {
    case "courseName":
    case "name":
      return row.courseName || row.course?.displayname || "";
    case "finalGrade":
    case "grade":
      return Number(String(row.finalGrade || "").replace(/,/g, ".").match(/-?\d+(?:\.\d+)?/)?.[0] ?? -Infinity);
    case "progress":
      return row.progressPercent ?? -Infinity;
    case "date":
    case "startdate":
      return row.startdate || row.course?.startdate || 0;
    case "enddate":
      return row.enddate || row.course?.enddate || 0;
    case "certificate":
      return row.certificate?.available ? 1 : 0;
    default:
      return row[key] ?? "";
  }
};

export const sortProgressRows = (rows, sortKey = "courseName", sortDirection = "asc") => {
  const direction = sortDirection === "desc" ? -1 : 1;
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const aValue = valueForSort(a, sortKey);
    const bValue = valueForSort(b, sortKey);

    if (typeof aValue === "string" || typeof bValue === "string") {
      return String(aValue).localeCompare(String(bValue)) * direction;
    }
    if (aValue === bValue) return 0;
    return aValue > bValue ? direction : -direction;
  });
};
